import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

import { useDimensions } from "./useDimensions";
import useIsMobile from "./useIsMobile";

const DeathPenalty = ({ japanData, usaData, taiwanData, chinaData }) => {
    const svgRef = useRef();
    const [selectedData, setSelectedData] = useState(chinaData);
    const [selectedCountry, setSelectedCountry] = useState("china");

    const isMobile = useIsMobile();


    const containerRef = useRef();
    const dimensions = useDimensions(containerRef);

    const customOrder = ["不详", "死刑", "当场死亡", "自杀身亡", "当场击毙", "死缓", "有期徒刑", "强制医疗", "可能免罪", "无期徒刑" ]

    const colors = {
        china: "var(--china)",
        taiwan: "var(--taiwan)",
        japan: "var(--japan)",
        usa: "var(--usa)"
    }

    const countryLabels = {
        china: "中国",
        taiwan: "台湾",
        japan: "日本",
        usa: "美国",
    };

    useEffect(() => {
        if (!selectedData || selectedData.length === 0) return;
        
        const resultCounts = d3.rollup(selectedData, v => v.length, d => d.results);
        const resultArray = Array.from(resultCounts, ([result, count]) => ({ result, count }));

        resultArray.sort((a, b) => b.count - a.count);

        const { width, height } = dimensions;
        const margin = { top: 30, right: 10, bottom: 60, left: 20 };
        
        const xScale = d3.scaleBand() 
            // .domain(customOrder)
            .domain(resultArray.map(d => d.result))
            .range([0, width - margin.left - margin.right])
            .padding(0.6);

        const yScale = d3.scaleLinear()  
            // .domain([0, d3.max(resultArray, d => d.count)])
            .domain([0, 55])
            .range([height - margin.top - margin.bottom, 0]);

        const svg = d3.select(svgRef.current)
            .attr("width", width)
            .attr("height", height);

        svg.selectAll("g").remove();

        const chartGroup = svg.append("g")
            .attr("transform", `translate(${margin.left}, ${margin.top})`);

        chartGroup.append("g")
            .attr("class", "svg-grid")
            .call(
                d3.axisLeft(yScale)
                .ticks(5)
                .tickSize(-width + margin.left + margin.right)
                .tickFormat("")
            )
            .select(".domain")
            .attr("stroke", "none");

            
        const Tooltip = d3.select("body")
            .append("div")
            .attr("class", "tooltip")
            .style("pointer-events", "none")
            .style("opacity", 0);

        const handleMouseOver = (event, d) => {
        
              Tooltip.style("opacity", 1);
              d3.select(event.currentTarget)
                .attr("stroke", "#fff");
            };

        const handleMouseMove = (event, d) => {
            Tooltip
                .html(`
                    <span style="color: ${colors[selectedCountry]}; font-weight: bold;">${countryLabels[selectedCountry]}</span> </br>
                    后续：<b>${d.result}</b>（<span class="tooltip__num">${d.count}</span>事件）
                    `)
                .style("left", (event.pageX + 10) + "px")
                .style("top", (event.pageY - 10) + "px");
        };

        const handleMouseOut = (event) => {
        
            Tooltip.style("opacity", 0);
            d3.select(event.currentTarget)
            .attr("stroke", () => colors[selectedData === chinaData ? "china" : selectedData === usaData ? "usa" : selectedData === taiwanData ? "taiwan" : "japan"]);
        };

        const bars = chartGroup.selectAll("rect")
            .data(resultArray)
            .enter()
            .append("rect")
            .attr("class", "bar")
            .attr("x", d => xScale(d.result))
            .attr("y", d => Math.min(yScale(d.count), height - margin.top - margin.bottom))
            .attr("width", xScale.bandwidth())
            .attr("height", d => Math.max(0, height - margin.top - margin.bottom - yScale(d.count)))
            // .attr("fill", "var(--china)")
            .attr("fill", () => colors[selectedData === chinaData ? "china" : selectedData === usaData ? "usa" : selectedData === taiwanData ? "taiwan" : "japan"])
            .attr("fill-opacity", 0.75)
            .attr("stroke", () => colors[selectedData === chinaData ? "china" : selectedData === usaData ? "usa" : selectedData === taiwanData ? "taiwan" : "japan"])
            .attr("stroke-opacity", 1);
            // .on("mouseover", handleMouseOver)
            // .on("mousemove", handleMouseMove)
            // .on("mouseout", handleMouseOut);

            bars
                .on("mouseover", handleMouseOver)
                .on("mousemove", handleMouseMove)
                .on("mouseout", handleMouseOut);

        // Add axes
        chartGroup.append("g")
            .attr("class", "x-axis")
            .attr("transform", isMobile ? `translate(-5, ${height - margin.top - margin.bottom})` : `translate(0, ${height - margin.top - margin.bottom})`)
            .call(d3.axisBottom(xScale)
                .tickPadding(10)
                .tickSize(0))
            .selectAll("text") // Select all x-axis labels
            .attr("text-anchor", isMobile ? "end" : "middle")
            .attr("transform", isMobile ? "rotate(-45)" : "rotate(0)")
            .each(function(d) { // Modify each label individually
                const text = d3.select(this);
                if (d === "无期徒刑不得假释") { 
                    text.text(null) // Clear original text
                        .append("tspan") // First line
                        .attr("x", 0)
                        .attr("dy", "0.5em") // Adjust spacing
                        .text("无期徒刑")
                        .append("tspan") // Second line
                        .attr("x", 0)
                        .attr("dy", "1.2em") // Move below first line
                        .text("不得假释");
                }
            });

        chartGroup.append("g")
            .attr("class", "y-axis")
            .call(d3.axisLeft(yScale)
                .ticks(5)
                .tickPadding(5)
                .tickSize(0)
                .tickFormat(d => d !== 0 ? d : ""))
            .select(".domain")
            .attr("opacity", 0);

        // return () => {
        //     d3.selectAll(".tooltip").remove();
        // };

    }, [selectedData, dimensions, selectedCountry]);

  return (
    <div className="center-container">
        <div className="article-body">
            <p className="subheader">死刑不能减少无差别杀人事件，</p>
            <h2>除了对罪犯判处死刑，社会还能做些什么？</h2>
            <div className="spacerdiv"/>
            <p>除去嫌疑人案发后身亡的案件，在经司法程序审判的无差别袭击案件中，我们看到中国的审判结果多为死刑，日本的情况与中国类似，但被判有期徒刑的比例也与之持平。而在台湾与美国，大多是有期徒刑和无期徒刑。这四个国家都仍保留死刑。</p>
           
        </div>

        <div ref={containerRef} style={{ height:"400px", width:"100%"}}>
            <h4>事件后续</h4>
            {/* Country Selection Buttons */}
            <div style={{ display: "flex",  alignItems: "center", justifyContent:"center", paddingTop:"15px" }}>
                    <div className="country-selection">
                        {Object.keys(colors).map((country, index) => (
                            <React.Fragment key={country}>
                                {index > 0 && <div className="vertical-divider" /> }

                                <button
                                    onClick={() => {
                                        setSelectedCountry(country);
                                        setSelectedData(eval(`${country}Data`));
                                    }}
                                    style={{
                                        background: selectedData === eval(`${country}Data`) ? "rgba(255,255,255,0.2)" : "transparent",
                                        fontWeight: selectedData === eval(`${country}Data`) ? "bold" : "normal",
                                        border: selectedData === eval(`${country}Data`) ? "1px solid white" : "none",
                                        borderRadius: "4px",
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "5px 10px",
                                        margin: "0 0.5px",
                                    }}
                                >
                                    <span
                                        className="country-color-indicator"
                                        style={{
                                            backgroundColor: colors[country],
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "50%",
                                            marginRight: "8px",
                                        }}
                                    ></span>
                                    {countryLabels[country]}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            <svg ref={svgRef}></svg>
        </div>

        <div className="article-body">
            <div className="spacerdiv__tall"/>

            <p>在无差别袭击案件中，死刑真的起到震慑作用吗？李厚辰是坚定的废死派，他认为，如果一个社会期待用死刑去宽慰人心，那是“社会的病态”。的确，执行死刑可能对受害人家属在情感上得到很大的释放，但比起死刑，他们可能更需要的是生活和经济上的赔偿。尤其对于失去独生子女或失去经济支柱的家庭，现金与保险补偿更有必要。</p>
            <p>中国目前仍尚未建立刑事被害人国家补偿制度，而这一制度在日本台湾和美国等其它国家早已建立。其实这项政策在法律界和政界已<a href="http://www.cicjc.com.cn/info/1040/16085.htm" target="_blank">讨论</a>甚久，早在2007年，时任江西省检察院检察长孙谦，就在两会期间提出<a href="http://big5.www.gov.cn/gate/big5/www.gov.cn///2007lh/content_549134.htm" target="_blank">议案</a>。他<a href="https://news.sina.com.cn/o/2007-03-14/080011408173s.shtml" target="_blank">说</a>，“（中国）有80%多的被害人无法得到实际赔偿。”他强调，“被害人如得不到应有的救济，还会加剧其报复心，也会增加社会的不稳定性。” 歪脑在梳理案件中看到，大多数新闻报道只提及政府会努力做好“善后工作”，但并未提及具体金额。</p>
            <p>另外，无差别袭击事件有一类嫌疑人患有精神疾病。与其它国家类似，他们大多被强制医疗。我们看到中国无差别袭击事件在过去的二十多年中的起伏，在2024年达到顶峰。斯坦福中国经济与制度研究中心（Stanford Center on China's Economy and Institutions）研究员吴国光在《<a href="https://www.bumingbai.net/2024/12/128-wgg-text/" target="_blank">不明白</a>》播客中，在提到这个话题时，说自己要给出一个最简单也最不让人乐观的答案，“它不会只出现在2024年，2024年只是一个开头。”</p>
            <p>如果真的是这样，那社会还能做些什么来减少这类悲剧发生的可能性？台湾大学法律学教授李茂生曾说，政府如果对于无差别袭击者不再教育或加以研究, 只在社会动荡时执行死刑, 就如同“国家的无差别杀人”。</p>
            <p>弗里德曼认为无差别袭击事件和自杀案件类似，都反映了社会心理健康资源的匮乏。中国需要培训更多接受过专业培训的心理健康专业人员，让不同阶层背景的城乡居民都能获得服务。但更难的是，是文化上的适应——对于自己的心理健康危机，人们能够自如地谈起这个话题，这对于中年男性，是更具挑战性的。而日本和台湾的学者也提出了人际关系建设、家庭重建、教育和职场改善等<a href="https://www.airitilibrary.com/Article/Detail/18104045-201612-201703210006-201703210006-113-123" target="_blank">预防策略</a>。</p>
            <p>也有中国学者指出，政府以一元化的行政方式控制和管理着多元化的社会，会存在很多盲点，需要有民间组织和博弈群体，在遇到征地拆迁、企业改制、工伤赔偿、教育医疗等问题，需要有为弱势的社会个体代言的组织。</p>
            <p>弗里德曼承认无差别袭击事件的复杂性，他也理解某些中国官员试图掩盖的动机，但他认为，“作为一个社会，必须学会有信心，面对自身的问题，并找出解决方法。但不幸的是，看起来事情的发展走向并非如此。”</p>
        </div>
        
    </div>
  );
}

export default DeathPenalty;
